import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "Reality Twin runs a public MCP (Model Context Protocol) server, so AI assistants like ChatGPT and Claude — and any agent — can use the network on a user's behalf. This is what makes your company reachable by other people's AI, not just their browser.",
  },
  { type: "h2", text: "Endpoint" },
  { type: "code", lang: "text", code: "https://realitytwin.io/mcp" },
  {
    type: "p",
    text: "It speaks the full MCP streamable-HTTP protocol. REST companions are available for simple integrations.",
  },
  { type: "h2", text: "Tools" },
  {
    type: "table",
    head: ["Tool", "What it does"],
    rows: [
      ["search_marketplace", "Find public companies on the network by query, industry or country."],
      [
        "ask_twin",
        "Ask any public company's AI Twin a question — answers are grounded and cite sources.",
      ],
      [
        "submit_lead",
        "Hand a company contact details with the user's consent (deduped, notifies the owner).",
      ],
      ["about_reality_twin", "Explain what Reality Twin is."],
      ["list_public_pages", "List the public pages an assistant can reference."],
    ],
  },
  { type: "h2", text: "Connect ChatGPT or Claude" },
  {
    type: "p",
    text: (
      <>
        The fastest path is the guided page at{" "}
        <Link to="/connect" className="underline hover:text-foreground">
          /connect
        </Link>
        : copy the MCP URL and follow the steps for your assistant (ChatGPT Developer-mode
        connectors, or Claude custom connectors).
      </>
    ),
  },
  { type: "h2", text: "Metering & limits" },
  {
    type: "p",
    text: "ask_twin spends the same LLM credits as website chat and draws from the target account's shared conversation pool. All tools are rate-limited per caller. submit_lead validates the company and email and blocks duplicates.",
  },
  {
    type: "callout",
    title: "Why it matters",
    text: "As buyers increasingly research through AI assistants, an MCP-reachable twin means your company can be found, questioned and contacted inside those assistants — a channel most competitors don't have.",
  },

  { type: "h2", text: "Agent Card (A2A discovery)" },
  {
    type: "p",
    text: "MCP is how an assistant talks to the network. The Agent Card is how any external agent discovers what a company is and what it can do — without a Reality Twin account. It is a plain JSON document served CORS-open, so an agent can fetch it directly from a browser or a server.",
  },
  {
    type: "table",
    head: ["Document", "Address", "What it describes"],
    rows: [
      [
        "Network card",
        "/.well-known/agent.json",
        "The Reality Twin network itself: skills (search the network, ask a twin, hand over a lead) and how to reach them.",
      ],
      [
        "Company card",
        "/api/public/agent/<companyId>",
        "One company: identity and legal form, verification status, trust proofs, response reputation, capabilities and endpoints.",
      ],
    ],
  },
  {
    type: "code",
    lang: "bash",
    code: "# The network\ncurl https://realitytwin.io/.well-known/agent.json\n\n# A specific company (id from search_marketplace or the profile URL)\ncurl https://realitytwin.io/api/public/agent/<companyId>",
  },
  {
    type: "p",
    text: "The company card is the answer to the first question of any purchase: does this company exist, is it who it claims to be, and does it actually respond? Verification carries the legal form — so an unregistered sole proprietorship reads as exactly that, rather than as a missing declaration — and reputation is only published once there is enough history to be fair.",
  },
  {
    type: "callout",
    title: "Machine-readable full text",
    text: "For everything else we publish — every support article, every competitor comparison and the honest live/beta/planned status of each feature — fetch /llms-full.txt. It's one plain Markdown document, no JavaScript required.",
  },
];

export const Route = createFileRoute("/docs/mcp-server")({
  head: () =>
    buildSeo({
      path: "/docs/mcp-server",
      title: "MCP Server — Reality Twin Docs",
      description:
        "Connect ChatGPT, Claude and agents to the Reality Twin network over MCP. Tools, endpoint, limits.",
    }),
  component: () => (
    <DocsPageShell
      slug="mcp-server"
      title="MCP server"
      description="Connect ChatGPT, Claude and AI agents to the network over the Model Context Protocol."
      blocks={blocks}
    />
  ),
});
